import React from 'react';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import AdminContainer from '../../Utility/AdminContainer';
import AdminBox from '../../Utility/AdminBox';
import AdminViewHeader from '../../Utility/AdminViewHeader';
import AdminViewContent from '../../Utility/AdminViewContent';
import useKnowledgebases from '../../../Hooks/useKnowledgebases';
import { RootState, useAppSelector } from '../../../store';
import { UserSettings } from '../../../Types';
import { Button } from '../../Utility/Button';
import { PaginationListType } from '../../../Types/PaginationParams';
import { Knowledgebase } from '../../../Types';
import { knowledgebaseService } from '../../../Services/knowledgebaseService';
import PaginatedView from '../../Utility/PaginatedView';

const Knowledgebases: React.FC = () => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const { KnowledgebasesActions, handleNavigate, i18n, windowWidth, getBreakpoint } = useKnowledgebases();

    const GRID_COLS = 'grid-cols-[minmax(157px,300px),minmax(157px,200px),minmax(70px,70px),minmax(157px,1fr)]';

    return (
        <div className="admin-container flex relative flex-col">
            <AdminContainer>
                <AdminBox classString="px-4">
                    <PaginatedView
                        listType={PaginationListType.Knowledgebases}
                        itemsFunction={knowledgebaseService.getKnowledgebasesByPage}
                        itemsPerPage={15}
                        renderItem={(kb: Knowledgebase, index: number) => (
                            <AdminViewContent
                                key={kb.identifier}
                                mobile={windowWidth < getBreakpoint('md')}
                                identifier={kb.identifier}
                                gridCols={GRID_COLS}
                                cols={{
                                    optionalCols: [
                                        {
                                            content: kb.name,
                                        },
                                        {
                                            content: `${kb.documentCount} ${i18n.translate({
                                                id: 'list.documentCount',
                                                sourceName: 'Knowledgebases',
                                            })}`,
                                        },
                                        {
                                            className: 'flex items-center justify-center w-full',
                                            content: (
                                                <div
                                                    className={`w-3 h-3 rounded-full ${
                                                        kb.status === 'in_progress'
                                                            ? 'bg-orange-400'
                                                            : kb.status === 'error'
                                                            ? 'bg-red-500'
                                                            : 'bg-green-500'
                                                    }`}
                                                    title={i18n.translate({
                                                        id: 'status.' + kb.status,
                                                        sourceName: 'Knowledgebases',
                                                    })}
                                                />
                                            ),
                                        },
                                    ],
                                    button: {
                                        buttons: [
                                            {
                                                variant: 'primaryRound',
                                                onClick: () => handleNavigate(kb.identifier),
                                                icon: <PencilSimpleIcon />,
                                            },
                                            {
                                                variant: 'secondaryRound',
                                                onClick: () =>
                                                    KnowledgebasesActions.handleOpenConfirmDialog(kb.identifier),
                                                icon: <TrashIcon />,
                                                demo: demo,
                                            },
                                        ],
                                    },
                                }}
                            />
                        )}
                    >
                        {({ list, filter }) => (
                            <>
                                {filter}
                                <div className="mb-4 md:hidden w-full">
                                    {(!settings.demo || userSettings.isSuperAdmin) && (
                                        <Button className="w-full mb-2" onClick={() => handleNavigate()}>
                                            {i18n.translate({ id: 'button.new', sourceName: 'Knowledgebases' })}
                                        </Button>
                                    )}
                                </div>
                                <div className="hidden md:block">
                                    <AdminViewHeader
                                        gridCols={GRID_COLS}
                                        cols={{
                                            cols: [
                                                {
                                                    text: i18n.translate({
                                                        id: 'adminHeader.mainCol',
                                                        sourceName: 'Main',
                                                    }),
                                                },
                                                {
                                                    text: i18n.translate({
                                                        id: 'list.documentCount',
                                                        sourceName: 'Knowledgebases',
                                                    }),
                                                },
                                                {
                                                    className: 'flex items-center justify-center w-full',
                                                    text: i18n.translate({
                                                        id: 'list.status',
                                                        sourceName: 'Knowledgebases',
                                                    }),
                                                },
                                            ],
                                            button:
                                                !settings.demo || userSettings.isSuperAdmin
                                                    ? {
                                                          text: i18n.translate({
                                                              id: 'button.new',
                                                              sourceName: 'Knowledgebases',
                                                          }),
                                                          function: () => handleNavigate(),
                                                      }
                                                    : undefined,
                                        }}
                                    />
                                    <hr className="border-layout-borderColor mt-5" />
                                </div>
                                {list}
                            </>
                        )}
                    </PaginatedView>
                </AdminBox>
            </AdminContainer>
        </div>
    );
};

export default Knowledgebases;
